import {
  GridActionsCellItem,
  GridColumns,
  GridNativeColTypes,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdEdit } from 'react-icons/md'
import { MdRefresh } from 'react-icons/md'
import GenericTable from '../../../components/table/GenericTable'
import { GenericToggle } from '../../../components/GenericToggle'
import { QueryKeyT } from '@lovoldsystem/core.client'
import { GpsToDms } from 'src/components/GpsToDms'
import { DialogContextType } from 'src/enums'
import { useRegenerateLocalityMapMutation } from 'src/api/localities'
import { toast } from 'react-toastify'
import { Spinner } from 'src/components/Spinner'

interface LocalitiesTableProps {
  getDetailPanelContent?: (params: any) => JSX.Element
  queryUrl: string
  generateQueryKey?: (page: number, pageSize: number) => QueryKeyT | undefined
  queryParams?: object
  onUpdate?: (locality: any) => void
  customerId: EntityId
  autoheight?: boolean
  pageSize?: number
}

export const LocalitiesTable: React.FC<LocalitiesTableProps> = ({
  getDetailPanelContent,
  queryUrl,
  queryParams,
  generateQueryKey,
  onUpdate,
  customerId,
  autoheight = false,
  pageSize,
}) => {
  const { t: translate } = useTranslation(['admin-locality', 'common'])
  const [regeneratingLocalityIds, setRegeneratingLocalityIds] = useState<Set<number>>(new Set())

  const { mutateAsync: regenerateMap } = useRegenerateLocalityMapMutation(customerId as EntityId)

  const onRegenerateMap = useCallback(
    async (localityId: number) => {
      setRegeneratingLocalityIds(prev => new Set(prev).add(localityId))
      try {
        await regenerateMap(localityId as EntityId)
        toast.success(translate('mapRegenerationSuccessfully', { ns: 'customer-locality' }))
      } catch (error) {
        toast.error(translate('mapRegenerationFailed', { ns: 'customer-locality' }))
      } finally {
        setRegeneratingLocalityIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(localityId)
          return newSet
        })
      }
    },
    [translate, regenerateMap],
  )

  const renderActions = useCallback(
    ({ row }) => {
      const actions: JSX.Element[] = []
      const isRegenerating = regeneratingLocalityIds.has(row.id)

      if (onUpdate) {
        actions.push(
          <GridActionsCellItem
            key={`edit-${row.id}`}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate && onUpdate(row)}
            color="primary"
            showInMenu
          />,
        )
      }

      actions.push(
        <GridActionsCellItem
          key={`refresh-${row.id}`}
          label={
            isRegenerating ? translate('regenerating', { ns: 'common' }) : translate('regenerate-map', { ns: 'common' })
          }
          icon={isRegenerating ? <Spinner size={24} /> : <MdRefresh size={24} />}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onRegenerateMap(row.id)
          }}
          color="primary"
          showInMenu
          disabled={isRegenerating}
        />,
      )

      return actions
    },
    [onUpdate, onRegenerateMap, translate, regeneratingLocalityIds],
  )

  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        align: 'center',
        renderCell: params => (
          <GenericToggle
            id={params.id}
            value={params.value}
            expandTitle={translate('view-locality', { ns: 'locality' })}
          />
        ),
      },
      {
        field: 'name',
        headerName: translate('name', { ns: 'common' }),
        flex: 1,
        cellClassName: 'font-weight-semi-bold',
         renderCell: (params) => (
    <span style={{ textTransform: 'capitalize' }}>
      {params.value}
    </span>
  )
      },
      {
        field: 'locationNr',
        headerName: translate('locationNr', { ns: 'admin-locality' }),
        flex: 1,
      },
      {
        field: 'latitude',
        headerName: translate('latitude', { ns: 'common' }),
        flex: 1,
        renderCell: ({ value }) => <GpsToDms decimalGPS={value} isLongitude={false} />,
      },
      {
        field: 'longitude',
        headerName: translate('longitude', { ns: 'common' }),
        flex: 1,
        renderCell: ({ value }) => <GpsToDms decimalGPS={value} isLongitude={true} />,
      },
      {
        field: 'active',
        headerName: translate('active', { ns: 'common' }),
        flex: 1,
        renderCell: ({ value: active }) => {
          return active ? translate('yes', { ns: 'common' }) : translate('no', { ns: 'common' })
        },
      },
    ],
    [translate],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: '',
        width: 60,
        getActions: renderActions,
      },
    ],
    [STATIC_COLUMNS, renderActions],
  )

  return (
    <GenericTable
      queryUrl={queryUrl}
      queryParams={queryParams}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
      columns={columns.filter(col => onUpdate || col.field !== 'actions')}
      id={'localities-table'}
      enableToolbar={true}
      enableServerPagination
      generateQueryKey={generateQueryKey}
      autoHeight={false}
      getRowHeight={() => 50}
      height={window.innerHeight - 230}
      contextType={DialogContextType.customerAdminContext}
      pageSize={pageSize}
    />
  )
}
